/**
 * AuthContext for BE Study Automation System
 * Enhanced authentication with role-based access control
 */

import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ROUTES, getDefaultRouteForRole } from '@/constants/routes';
import { USER_ROLES, MOCK_USERS, USER_STATUS } from '@/constants/beStudyMockData';
import { 
  hasPermission, 
  hasAllPermissions, 
  hasAnyPermission,
  canPerformEditorAction,
  getEditorActionsForRole,
  canAccessDocument,
  canEditDocument,
  canDeleteDocument,
  canReviewDocument,
  canAccessRoute,
  PERMISSIONS,
} from '@/constants/permissions';
import { getNavigationByRole } from '@/constants/navigation';

// Storage keys
const STORAGE_KEYS = {
  SESSION: 'be_study_session',
  USER: 'be_study_user',
};

// Create AuthContext
export const AuthContext = createContext(null);

/**
 * AuthProvider Component
 * Provides authentication state and methods with role-based permissions
 */
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  // Derived state
  const isAdmin = useMemo(() => currentUser?.role === USER_ROLES.ADMIN, [currentUser]);
  const isUser = useMemo(() => currentUser?.role === USER_ROLES.USER, [currentUser]);
  const isReviewer = useMemo(() => currentUser?.role === USER_ROLES.REVIEWER, [currentUser]);
  const userRole = useMemo(() => currentUser?.role || null, [currentUser]);

  /**
   * Initialize auth state from localStorage on mount
   */
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedSession = localStorage.getItem(STORAGE_KEYS.SESSION);
        const storedUser = localStorage.getItem(STORAGE_KEYS.USER);

        if (storedSession && storedUser) {
          const sessionData = JSON.parse(storedSession);
          const userData = JSON.parse(storedUser);

          // Check if session has expired
          const now = new Date();
          const expiresAt = new Date(sessionData.expiresAt);

          if (now > expiresAt) {
            // Session expired, clear storage
            localStorage.removeItem(STORAGE_KEYS.SESSION);
            localStorage.removeItem(STORAGE_KEYS.USER);
          } else {
            setCurrentUser(userData);
            setSession(sessionData);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear potentially corrupted data
        localStorage.removeItem(STORAGE_KEYS.SESSION);
        localStorage.removeItem(STORAGE_KEYS.USER);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login function with BE Study mock users
   * @param {string} email - User email
   * @param {string} password - Password (for mock, we use a simple check)
   * @returns {Object} Result object with success status and data/error
   */
  const login = useCallback((email, password) => {
    setLoading(true);

    try {
      // Find user by email (case-insensitive)
      const user = MOCK_USERS.find(
        u => u.email.toLowerCase() === email.toLowerCase()
      );

      if (!user) {
        return {
          success: false,
          error: 'User not found. Please check your email.',
        };
      }

      // Check user status
      if (user.status === USER_STATUS.INACTIVE) {
        return {
          success: false,
          error: 'Your account is inactive. Please contact administrator.',
        };
      }

      // For mock purposes, accept any password or use 'password123'
      if (password !== 'password123' && password !== 'admin' && password !== 'test') {
        // In production, this would validate against hashed password
        // For demo, we accept common test passwords
      }

      // Create session
      const newSession = {
        userId: user.id,
        email: user.email,
        role: user.role,
        loginTime: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
      };

      // Create user object (without sensitive data)
      const userData = {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        department: user.department,
        avatar: user.avatar,
      };

      // Save to localStorage
      localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(newSession));
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));

      // Update state
      setCurrentUser(userData);
      setSession(newSession);
      setIsAuthenticated(true);

      return {
        success: true,
        user: userData,
        redirectTo: getDefaultRouteForRole(user.role),
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'An unexpected error occurred during login.',
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout function
   */
  const logout = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.SESSION);
      localStorage.removeItem(STORAGE_KEYS.USER);
      setCurrentUser(null);
      setSession(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, []);

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(() => {
    try {
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setCurrentUser(userData);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  }, []);

  /**
   * Check if current user has a specific role
   * @param {string} role - Role to check
   * @returns {boolean}
   */
  const hasRole = useCallback((role) => {
    if (!currentUser) return false;
    // Admin has all roles
    if (currentUser.role === USER_ROLES.ADMIN) return true;
    return currentUser.role === role;
  }, [currentUser]);

  /**
   * Check if current user has a specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean}
   */
  const checkPermission = useCallback((permission) => {
    if (!currentUser) return false;
    return hasPermission(currentUser.role, permission);
  }, [currentUser]);

  /**
   * Check if current user has all specified permissions
   * @param {string[]} permissions - Array of permissions
   * @returns {boolean}
   */
  const checkAllPermissions = useCallback((permissions) => {
    if (!currentUser) return false;
    return hasAllPermissions(currentUser.role, permissions);
  }, [currentUser]);

  /**
   * Check if current user has any of the specified permissions
   * @param {string[]} permissions - Array of permissions
   * @returns {boolean}
   */
  const checkAnyPermission = useCallback((permissions) => {
    if (!currentUser) return false;
    return hasAnyPermission(currentUser.role, permissions);
  }, [currentUser]);

  /**
   * Check if current user can perform an editor action
   * @param {string} action - Editor action
   * @returns {boolean}
   */
  const checkEditorAction = useCallback((action) => {
    if (!currentUser) return false;
    return canPerformEditorAction(currentUser.role, action);
  }, [currentUser]);

  /**
   * Get available editor actions for current user
   * @returns {string[]}
   */
  const getAvailableEditorActions = useCallback(() => {
    if (!currentUser) return [];
    return getEditorActionsForRole(currentUser.role);
  }, [currentUser]);

  /**
   * Check if current user can access a document
   * @param {object} document - Document object
   * @returns {boolean}
   */
  const checkDocumentAccess = useCallback((document) => {
    if (!currentUser) return false;
    return canAccessDocument(currentUser, document);
  }, [currentUser]);

  /**
   * Check if current user can edit a document
   * @param {object} document - Document object
   * @returns {boolean}
   */
  const checkDocumentEdit = useCallback((document) => {
    if (!currentUser) return false;
    return canEditDocument(currentUser, document);
  }, [currentUser]);

  /**
   * Check if current user can delete a document
   * @param {object} document - Document object
   * @returns {boolean}
   */
  const checkDocumentDelete = useCallback((document) => {
    if (!currentUser) return false;
    return canDeleteDocument(currentUser, document);
  }, [currentUser]);

  /**
   * Check if current user can review a document
   * @param {object} document - Document object
   * @returns {boolean}
   */
  const checkDocumentReview = useCallback((document) => {
    if (!currentUser) return false;
    return canReviewDocument(currentUser, document);
  }, [currentUser]);

  /**
   * Check if current user can access a route
   * @param {string} path - Route path
   * @returns {boolean}
   */
  const checkRouteAccess = useCallback((path) => {
    if (!currentUser) return false;
    return canAccessRoute(currentUser.role, path);
  }, [currentUser]);

  /**
   * Get navigation items for current user
   * @returns {Array}
   */
  const getNavigation = useCallback(() => {
    if (!currentUser) return [];
    return getNavigationByRole(currentUser.role);
  }, [currentUser]);

  /**
   * Get default route for current user
   * @returns {string}
   */
  const getDefaultRoute = useCallback(() => {
    if (!currentUser) return ROUTES.LOGIN;
    return getDefaultRouteForRole(currentUser.role);
  }, [currentUser]);

  const value = {
    // State
    currentUser,
    isAuthenticated,
    loading,
    session,
    
    // Role flags
    isAdmin,
    isUser,
    isReviewer,
    userRole,
    
    // Auth methods
    login,
    logout,
    refreshUser,
    
    // Role checks
    hasRole,
    
    // Permission checks
    checkPermission,
    checkAllPermissions,
    checkAnyPermission,
    
    // Editor action checks
    checkEditorAction,
    getAvailableEditorActions,
    
    // Document access checks
    checkDocumentAccess,
    checkDocumentEdit,
    checkDocumentDelete,
    checkDocumentReview,
    
    // Route access
    checkRouteAccess,
    
    // Navigation
    getNavigation,
    getDefaultRoute,
    
    // Constants (for convenience)
    roles: USER_ROLES,
    permissions: PERMISSIONS,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
